import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from "@/components/molecules/Tooltip";
import { cn } from "@/lib/utils";
import * as React from "react";

export type ToggleTabProps = React.PropsWithChildren<{
  isSelected: boolean;
  onSelect: (value: string) => void;
  disabled?: boolean;
  tab: { label: React.ReactNode; value: any; tooltip?: React.ReactNode };
  className?: string;
}>;

export const ToggleTabButton = React.forwardRef<
  HTMLButtonElement,
  ToggleTabProps
>(
  (
    { isSelected, onSelect, children, className, disabled, tab, ...props },
    ref,
  ) => {
    return (
      <button
        ref={ref}
        role="switch"
        aria-checked={isSelected}
        data-selected={isSelected}
        data-value={tab.value}
        disabled={disabled}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(tab.value);
          }
        }}
        className={cn(
          "tw-text-gray-600 tw-w-full tw-text-center tw-rounded-4 tw-body-1-m tw-p-8 tw-cursor-pointer hover:tw-text-gray-900 disabled:tw-text-gray-500 tw-inline-block tw-toggle-tab tw-relative before:tw-absolute before:tw-w-[1px] before:tw-left-[-2.5px] before:tw-top-0 before:tw-h-full before:tw-bg-gray-500 first:before:tw-hidden data-[selected=true]:before:tw-hidden data-[hide-divider=true]:before:tw-hidden",
          {
            "tw-bg-base-white tw-text-gray-900 tw-shadow-sm": isSelected,
          },
          className,
        )}
        {...props}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault();
          onSelect(tab.value);
        }}
      >
        {tab.label}
      </button>
    );
  },
);
ToggleTabButton.displayName = "ToggleTabButton";

const ToggleTabWithTooltip = React.forwardRef<
  HTMLButtonElement,
  ToggleTabProps
>(({ disabled, tab, onSelect, className, isSelected, ...props }, ref) => {
  return (
    <Tooltip>
      <TooltipTrigger className="tw-w-full" asChild={true}>
        <ToggleTabButton
          ref={ref}
          tab={tab}
          isSelected={isSelected}
          disabled={disabled}
          onSelect={onSelect}
          className={className}
          {...props}
        >
          {tab.label}
        </ToggleTabButton>
      </TooltipTrigger>

      <TooltipContent>
        {tab.tooltip}
        <TooltipArrow className="tw-fill-base-white" />
      </TooltipContent>
    </Tooltip>
  );
});
ToggleTabWithTooltip.displayName = "ToggleTabWithTooltip";

export const ToggleTab = React.forwardRef<HTMLButtonElement, ToggleTabProps>(
  ({ tab, className, disabled, onSelect, isSelected, ...props }, ref) => {
    return tab.tooltip ? (
      <ToggleTabWithTooltip
        ref={ref}
        tab={tab}
        isSelected={isSelected}
        disabled={disabled}
        onSelect={onSelect}
        className={className}
        {...props}
      />
    ) : (
      <ToggleTabButton
        ref={ref}
        tab={tab}
        isSelected={isSelected}
        disabled={disabled}
        onSelect={onSelect}
        className={className}
        {...props}
      >
        {tab.label}
      </ToggleTabButton>
    );
  },
);
ToggleTab.displayName = "ToggleTab";
